import { MutableRefObject, useLayoutEffect, useRef } from 'react'

import { ENTER_MS, ManagedToast, STACK_SHIFT_MS } from './use-managed-toasts'

type ToastRect = {
  top: number
  left: number
  width: number
}

const PINNED_Z_INDEX = '10000'

const PIN_STYLE_KEYS = ['position', 'top', 'left', 'width', 'zIndex'] as const

const clearPinStyles = (element: HTMLElement) => {
  PIN_STYLE_KEYS.forEach(key => {
    element.style[key] = ''
  })
}

const runEnterFlip = (element: HTMLElement) => {
  element.style.transition = 'none'
  element.style.opacity = '0'
  element.style.transform = 'translateY(50%)'

  requestAnimationFrame(() => {
    element.style.transition = `transform ${ENTER_MS}ms ease-out, opacity ${ENTER_MS}ms ease-out`
    element.style.transform = 'translateY(0)'
    element.style.opacity = '1'

    window.setTimeout(() => {
      element.style.transition = ''
      element.style.transform = ''
      element.style.opacity = ''
    }, ENTER_MS)
  })
}

const runShiftFlip = (element: HTMLElement, deltaY: number) => {
  element.style.transition = 'none'
  element.style.transform = `translateY(${deltaY}px)`

  requestAnimationFrame(() => {
    element.style.transition = `transform ${STACK_SHIFT_MS}ms ease`
    element.style.transform = 'translateY(0)'

    window.setTimeout(() => {
      element.style.transition = ''
      element.style.transform = ''
    }, STACK_SHIFT_MS)
  })
}

const pinExitingToast = (element: HTMLElement, prevRect: ToastRect) => {
  element.style.position = 'fixed'
  element.style.top = `${prevRect.top}px`
  element.style.left = `${prevRect.left}px`
  element.style.width = `${prevRect.width}px`
  element.style.zIndex = PINNED_Z_INDEX
}

export function useToastFlip(
  toasts: ManagedToast[],
  itemRefs: MutableRefObject<Map<string, HTMLElement>>
) {
  const prevPositionsRef = useRef<Map<string, ToastRect>>(new Map())
  const enterFlipStartedRef = useRef<Set<string>>(new Set())
  const pinnedIdsRef = useRef<Set<string>>(new Set())

  useLayoutEffect(() => {
    const refs = itemRefs.current
    const nextPositions = new Map<string, ToastRect>()
    const activeIds = new Set(toasts.map(toast => toast.notification.id))

    enterFlipStartedRef.current.forEach(id => {
      if (!activeIds.has(id)) {
        enterFlipStartedRef.current.delete(id)
      }
    })

    pinnedIdsRef.current.forEach(id => {
      if (!activeIds.has(id)) {
        const element = refs.get(id)
        if (element) {
          clearPinStyles(element)
        }
        pinnedIdsRef.current.delete(id)
      }
    })

    prevPositionsRef.current.forEach((_, id) => {
      if (!activeIds.has(id)) {
        prevPositionsRef.current.delete(id)
      }
    })

    toasts.forEach(toast => {
      if (toast.phase !== 'exiting') {
        return
      }

      const id = toast.notification.id
      if (pinnedIdsRef.current.has(id)) {
        return
      }

      const element = refs.get(id)
      const prevRect = prevPositionsRef.current.get(id)
      if (!element || !prevRect) {
        return
      }

      pinExitingToast(element, prevRect)
      pinnedIdsRef.current.add(id)
    })

    toasts.forEach(toast => {
      if (toast.phase === 'exiting') {
        return
      }

      const id = toast.notification.id
      const element = refs.get(id)
      if (!element) {
        return
      }

      if (toast.phase === 'entering') {
        if (!enterFlipStartedRef.current.has(id)) {
          enterFlipStartedRef.current.add(id)
          runEnterFlip(element)
        }
        return
      }

      const rect = element.getBoundingClientRect()
      const nextRect: ToastRect = {
        top: rect.top,
        left: rect.left,
        width: rect.width
      }
      nextPositions.set(id, nextRect)

      const previousRect = prevPositionsRef.current.get(id)
      if (!previousRect) {
        return
      }

      const deltaY = previousRect.top - nextRect.top
      if (Math.abs(deltaY) < 0.5) {
        return
      }

      runShiftFlip(element, deltaY)
    })

    prevPositionsRef.current = nextPositions
  }, [toasts, itemRefs])

  useLayoutEffect(() => {
    const refs = itemRefs.current

    return () => {
      pinnedIdsRef.current.forEach(id => {
        const element = refs.get(id)
        if (element) {
          clearPinStyles(element)
        }
      })
      pinnedIdsRef.current.clear()
    }
  }, [itemRefs])
}
